import React from 'react';

import { Icon, ScrollView } from '@rbilabs/universal-components';
import { Skeleton } from '@rbilabs/universal-components';
import { ProductSkeletonCard } from 'features/menu/components/menu-product-skeleton/product-skeleton-card';
import { ProductSkeletonListItem } from 'features/menu/components/menu-product-skeleton/product-skeleton-list-item';
import {
  CloseBtnWrapper,
  ProductContentSkeleton,
  ProductInfoSkeletonContainer,
  ProductOverviewSkeletonContainer,
  ProductSkeletonButton,
  ProductSkeletonCta,
  ProductSkeletonHeader,
} from 'features/menu/components/menu-product-skeleton/styled';
import { StyleSheet } from 'react-native';

import { InnerLayout, LayoutContainer } from 'components/layout/styled';
import { CurvedBackground } from 'components/product-detail-v2/product-overview/curved-bg';
import { Box } from 'components/ui';
import { MenuObjectTypes } from 'enums/menu';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { primitive } from 'styles/constants';

export const MenuProductSkeleton = ({
  type,
  hideHeader = false,
}: {
  type: string | null;
  /** Omit the decorative header when a real modal header already provides dismiss. */
  hideHeader?: boolean;
}) => {
  const isMobile = useIsMobileBreakpoint();

  const skeletonLoaderContent = React.useMemo(() => {
    switch (type) {
      case MenuObjectTypes.COMBO:
        return <ProductSkeletonListItem />;
      default:
        return <ProductSkeletonCard />;
    }
  }, [type]);

  return (
    <Box style={styles.container}>
      {hideHeader ? null : (
        <ProductSkeletonHeader testID="product-skeleton-header">
          <CloseBtnWrapper>
            <Icon mt={2} variant="cancel" />
          </CloseBtnWrapper>
          <Skeleton width={{ base: 'full', md: '1/3' }} h="$4" rounded="full" />
        </ProductSkeletonHeader>
      )}

      <Box style={styles.content}>
        <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
          <ProductOverviewSkeletonContainer>
            <CurvedBackground
              height={isMobile ? 200 : 170}
              backgroundColor={primitive.$blackOpacity4}
            />
          </ProductOverviewSkeletonContainer>

          <Box>
            <LayoutContainer isFullContainer={isMobile} marginY={0} paddingY={0}>
              <InnerLayout marginY={0} paddingY={0}>
                <ProductContentSkeleton>{skeletonLoaderContent}</ProductContentSkeleton>
              </InnerLayout>
            </LayoutContainer>
          </Box>

          <ProductInfoSkeletonContainer>
            <LayoutContainer isFullContainer={isMobile}>
              <InnerLayout>
                <Skeleton width="1/3" marginY="$2" h="$4" rounded="full" />
                {Array.from({ length: 5 }).map((_, index) => (
                  <Skeleton key={index} width="full" marginY="$2" h="$4" rounded="full" />
                ))}
              </InnerLayout>
            </LayoutContainer>
          </ProductInfoSkeletonContainer>
        </ScrollView>
      </Box>

      <ProductSkeletonCta testID="product-skeleton-cta">
        <ProductSkeletonButton>
          <Skeleton width="full" h="$4" rounded="full" />
        </ProductSkeletonButton>
      </ProductSkeletonCta>
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: primitive.$white,
    flex: 1,
  },
  content: {
    flex: 1,
  },
});
